import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Check, X, Trash2, Star, Clock, Shield,
  ChevronDown, ChevronUp, Upload, FileSpreadsheet,
} from "lucide-react";

export default function AdminClientPrices() {
  const { authAxios } = useAuth();
  const [pendingPrices, setPendingPrices] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPrices, setClientPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const [ruleStats, setRuleStats] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, clientsRes, statsRes] = await Promise.all([
        authAxios.get("/client-prices/pending"),
        authAxios.get("/clients"),
        authAxios.get("/discount-rules/stats").catch(() => ({ data: { total: 0, by_priority: {} } })),
      ]);
      setPendingPrices(pendingRes.data);
      setClients(clientsRes.data);
      setRuleStats(statsRes.data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (priceId) => {
    try {
      await authAxios.put(`/client-prices/${priceId}/approve`);
      toast.success("Prezzo approvato");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleReject = async (priceId) => {
    try {
      await authAxios.put(`/client-prices/${priceId}/reject`);
      toast.success("Prezzo rifiutato");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleApproveAll = async () => {
    for (const p of pendingPrices) {
      try {
        await authAxios.put(`/client-prices/${p.id}/approve`);
      } catch (e) { console.error(`Failed to approve price ${p.id}:`, e); }
    }
    toast.success("Tutti i prezzi approvati");
    fetchData();
  };

  const loadClientPrices = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
      setClientPrices([]);
      return;
    }
    try {
      const res = await authAxios.get(`/clients/${clientId}/prices`);
      setClientPrices(res.data);
      setExpandedClient(clientId);
    } catch {
      toast.error("Errore nel caricamento prezzi");
    }
  };

  const deleteClientPrice = async (clientId, priceId) => {
    try {
      await authAxios.delete(`/clients/${clientId}/prices/${priceId}`);
      toast.success("Prezzo eliminato");
      loadClientPrices(clientId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const toggleScontiFissi = async (clientId, current) => {
    try {
      await authAxios.put(`/clients/${clientId}/sconti-fissi`, { sconti_fissi: !current });
      toast.success(!current ? "Contrattualizzato attivato" : "Contrattualizzato disattivato");
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, sconti_fissi: !current } : c
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleImportRules = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/discount-rules/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} errori durante l'importazione`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore importazione");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 50);
    const q = clientSearch.toLowerCase();
    return clients.filter(c =>
      (c.ragione_sociale || "").toLowerCase().includes(q) ||
      (c.partita_iva || "").includes(q) ||
      (c.codice_cli || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [clients, clientSearch]);

  // Group pending by client
  const pendingByClient = useMemo(() => {
    const map = {};
    pendingPrices.forEach(p => {
      if (!map[p.client_id]) map[p.client_id] = { client_name: p.client_name, items: [] };
      map[p.client_id].items.push(p);
    });
    return Object.entries(map);
  }, [pendingPrices]);

  // Sorted rules by priority (memoized to avoid per-render sort)
  const rulesByPrioritySorted = useMemo(() => {
    if (!ruleStats?.by_priority) return [];
    return Object.entries(ruleStats.by_priority).sort(([a], [b]) => Number(a) - Number(b));
  }, [ruleStats]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Sconti Clienti" />
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-white mb-1">Sconti Clienti</h1>
          <p className="text-zinc-400 text-sm">Gestisci prezzi personalizzati e approvazioni</p>
        </div>

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-[#18181b] border border-white/5">
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-2" />
              Da Approvare
              {pendingPrices.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">{pendingPrices.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black" data-testid="tab-clients">
              <Star className="w-4 h-4 mr-2" />
              Per Cliente
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white" data-testid="tab-rules">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Regole
              {ruleStats?.total > 0 && (
                <Badge className="ml-2 bg-blue-500/50 text-white text-xs">{ruleStats.total}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending" className="space-y-4">
            {pendingPrices.length === 0 ? (
              <Card className="bg-[#18181b] border-white/5">
                <CardContent className="p-8 text-center">
                  <Check className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="text-zinc-400">Nessun prezzo in attesa di approvazione</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex justify-end">
                  <Button onClick={handleApproveAll}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="approve-all-btn">
                    <Check className="w-4 h-4 mr-2" /> Approva Tutti ({pendingPrices.length})
                  </Button>
                </div>
                {pendingByClient.map(([clientId, group]) => (
                  <Card key={clientId} className="bg-[#18181b] border-white/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base">{group.client_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/5 text-zinc-400 text-xs">
                              <th className="text-left p-3">Prodotto</th>
                              <th className="text-right p-3">Listino</th>
                              <th className="text-right p-3">Autorizzare</th>
                              <th className="text-right p-3 text-amber-400">Richiesto</th>
                              <th className="text-right p-3">Sconto</th>
                              <th className="text-center p-3">Agente</th>
                              <th className="p-3 w-24"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map(p => {
                              const discount = p.price_list > 0
                                ? ((1 - p.custom_price / p.price_list) * 100).toFixed(1)
                                : "-";
                              return (
                                <tr key={p.id} className="border-b border-white/5" data-testid={`pending-price-${p.id}`}>
                                  <td className="p-3">
                                    <p className="text-white">{p.product_name}</p>
                                    <p className="text-zinc-500 text-xs font-mono">{p.product_sku}</p>
                                  </td>
                                  <td className="p-3 text-right font-mono text-zinc-400">{(p.price_list || 0).toFixed(2)}</td>
                                  <td className="p-3 text-right font-mono text-purple-400">{(p.price_auth || 0).toFixed(2)}</td>
                                  <td className="p-3 text-right font-mono text-amber-400 font-bold">{p.custom_price.toFixed(2)}</td>
                                  <td className="p-3 text-right text-xs text-zinc-400">-{discount}%</td>
                                  <td className="p-3 text-center text-xs text-zinc-400">{p.created_by_name}</td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-1 justify-end">
                                      <Button size="sm" variant="ghost" onClick={() => handleApprove(p.id)}
                                        className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                        data-testid={`approve-${p.id}`}>
                                        <Check className="w-4 h-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => handleReject(p.id)}
                                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                        data-testid={`reject-${p.id}`}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Cerca cliente..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="pl-10 bg-[#18181b] border-white/10"
                data-testid="client-price-search"
              />
            </div>

            <div className="space-y-2">
              {filteredClients.map(c => (
                <Card key={c.id} className="bg-[#18181b] border-white/5">
                  <CardContent className="p-0">
                    <button
                      onClick={() => loadClientPrices(c.id)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
                      data-testid={`client-row-${c.id}`}
                    >
                      <div>
                        <p className="text-white font-medium text-sm">{c.ragione_sociale}</p>
                        <p className="text-zinc-500 text-xs">
                          {c.codice_cli && `${c.codice_cli} | `}
                          {c.partita_iva && `P.IVA: ${c.partita_iva}`}
                          {c.citta && ` - ${c.citta}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.sconti_fissi && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">
                            <Shield className="w-3 h-3 mr-1" /> Contrattualizzato
                          </Badge>
                        )}
                        {expandedClient === c.id ? (
                          <ChevronUp className="w-4 h-4 text-zinc-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-zinc-400" />
                        )}
                      </div>
                    </button>

                    {expandedClient === c.id && (
                      <div className="border-t border-white/5 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-zinc-400 text-sm">{clientPrices.length} prezzi personalizzati</span>
                          <Button
                            size="sm"
                            variant={c.sconti_fissi ? "destructive" : "outline"}
                            onClick={(e) => { e.stopPropagation(); toggleScontiFissi(c.id, c.sconti_fissi); }}
                            className={c.sconti_fissi
                              ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30"
                              : "border-white/10 text-zinc-400 hover:text-white"
                            }
                            data-testid={`toggle-sconti-fissi-${c.id}`}
                          >
                            <Shield className="w-3 h-3 mr-1" />
                            {c.sconti_fissi ? "Rimuovi Contrattualizzato" : "Imposta Contrattualizzato"}
                          </Button>
                        </div>

                        {clientPrices.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/5 text-zinc-400 text-xs">
                                  <th className="text-left p-2">Prodotto</th>
                                  <th className="text-right p-2">Listino</th>
                                  <th className="text-right p-2 text-emerald-400">Prezzo Cliente</th>
                                  <th className="text-right p-2">Sconto</th>
                                  <th className="p-2 w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {clientPrices.map(cp => {
                                  const discount = cp.price_list > 0
                                    ? ((1 - cp.custom_price / cp.price_list) * 100).toFixed(1)
                                    : "-";
                                  return (
                                    <tr key={cp.id} className="border-b border-white/5">
                                      <td className="p-2">
                                        <p className="text-white text-xs">{cp.product_name}</p>
                                        <p className="text-zinc-500 text-[10px] font-mono">{cp.product_sku}</p>
                                      </td>
                                      <td className="p-2 text-right font-mono text-zinc-400 text-xs">{(cp.price_list || 0).toFixed(2)}</td>
                                      <td className="p-2 text-right font-mono text-emerald-400 text-xs font-bold">{cp.custom_price.toFixed(2)}</td>
                                      <td className="p-2 text-right text-xs text-zinc-400">-{discount}%</td>
                                      <td className="p-2">
                                        <Button size="sm" variant="ghost"
                                          onClick={(e) => { e.stopPropagation(); deleteClientPrice(c.id, cp.id); }}
                                          className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                                          data-testid={`delete-price-${cp.id}`}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-zinc-500 text-sm text-center py-4">Nessun prezzo personalizzato</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Rules Tab */}
          <TabsContent value="rules" className="space-y-4">
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-blue-400" /> Regole Sconto Importate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input type="file" accept=".xls,.xlsx,.csv" onChange={handleImportRules} className="hidden" />
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${importing ? "bg-blue-500/20 text-blue-300" : "bg-blue-500 hover:bg-blue-600 text-white"}`}>
                      <Upload className="w-4 h-4" />
                      {importing ? "Importazione..." : "Importa File Sconti (.xls)"}
                    </span>
                  </label>
                </div>

                {ruleStats && ruleStats.total > 0 ? (
                  <div className="space-y-3">
                    <p className="text-zinc-400 text-sm">
                      <span className="text-white font-bold">{ruleStats.total}</span> regole sconto caricate
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/5 text-zinc-400 text-xs">
                            <th className="text-left p-2">Priorità</th>
                            <th className="text-left p-2">Tipo</th>
                            <th className="text-right p-2">Regole</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rulesByPrioritySorted.map(([p, info]) => (
                            <tr key={p} className="border-b border-white/5">
                              <td className="p-2">
                                <Badge className="bg-blue-500/20 text-blue-400 text-xs">{p}</Badge>
                              </td>
                              <td className="p-2 text-white text-xs">{info.label}</td>
                              <td className="p-2 text-right font-mono text-white">{info.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-zinc-500 text-xs">
                      La priorità 1 (Cliente-Articolo) ha la precedenza massima. In caso di conflitto, la regola con priorità più bassa vince.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                    <p className="text-zinc-400">Nessuna regola sconto importata</p>
                    <p className="text-zinc-500 text-xs mt-1">Importa un file .xls con le regole di sconto</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
